// src/components/my-journey/BenchHaunting.jsx
// The Haunting — parallel universe combining bench ghosts AND captain regrets
// Dual cumulative line chart (actual vs parallel) + square stat cards + compact event rows

import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { motion } from 'framer-motion';
import { useLeagueData, useManagerContext } from '../../context/DataContext';

// ── Design tokens ───────────────────────────────────────────
const COLOURS = {
  bg:        '#000006',
  blue:      '#3f69ae',
  turquoise: '#44d5a3',
  bad:       '#eb5757',
  good:      '#219653',
  grey:      '#8a8a8a',
  surface1:  '#1d1d1d',
  surface2:  '#272727',
  border:    '#4a4a4a',
};

// ── Chart layout constants ────────────────────────────────────────────────────
const PAD_LEFT   = 48;
const PAD_RIGHT  = 72;
const PAD_TOP    = 20;
const PAD_BOTTOM = 24;

// ── Format integer with commas (e.g. 1234 → "1,234") ─────────────────────────
const fmtPts = (n) =>
  typeof n === 'number' ? n.toLocaleString('en-AU') : '0';

// ── Animated counter — counts up from 0 using requestAnimationFrame ───────────
const AnimatedCounter = ({ target, colour = COLOURS.turquoise, fontSize = '52px' }) => {
  const [displayed, setDisplayed] = useState(0);
  const rafRef      = useRef(null);
  const startRef    = useRef(null);
  const DURATION_MS = 1400;

  useEffect(() => {
    if (target === 0) { setDisplayed(0); return; }
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    startRef.current = null;

    const tick = (ts) => {
      if (!startRef.current) startRef.current = ts;
      const progress = Math.min((ts - startRef.current) / DURATION_MS, 1);
      // Ease-out cubic
      const eased = 1 - Math.pow(1 - progress, 3);
      setDisplayed(Math.round(eased * target));
      if (progress < 1) rafRef.current = requestAnimationFrame(tick);
    };

    rafRef.current = requestAnimationFrame(tick);
    return () => { if (rafRef.current) cancelAnimationFrame(rafRef.current); };
  }, [target]);

  return (
    <span style={{ color: colour, fontWeight: 900, fontSize, lineHeight: 1, letterSpacing: '-1px' }}>
      {fmtPts(displayed)}
    </span>
  );
};

// ── Small square stat card ────────────────────────────────────────────────────
const SquareStatCard = ({ value, valueColour, label }) => (
  <div
    style={{
      width: '130px',
      height: '110px',
      backgroundColor: COLOURS.surface1,
      border: `1px solid ${COLOURS.border}`,
      borderRadius: '12px',
      padding: '14px 10px',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      textAlign: 'center',
      gap: '4px',
      boxSizing: 'border-box',
    }}
  >
    <p style={{ color: valueColour, fontWeight: 700, fontSize: '22px', lineHeight: 1, margin: 0 }}>
      {value}
    </p>
    <p style={{ color: COLOURS.grey, fontSize: '11px', fontWeight: 500, margin: 0, lineHeight: 1.3 }}>
      {label}
    </p>
  </div>
);

// ── Compact event row (captain regrets / bench ghosts list) ───────────────────
const EventRow = ({ emoji, label, bonus }) => (
  <motion.div
    variants={{ hidden: { opacity: 0, y: 8 }, visible: { opacity: 1, y: 0 } }}
    style={{
      display: 'flex',
      alignItems: 'center',
      gap: '10px',
      padding: '8px 12px',
      backgroundColor: COLOURS.surface1,
      border: '1px solid rgba(235,87,87,0.2)',
      borderRadius: '8px',
    }}
  >
    {/* Emoji icon */}
    <span style={{ fontSize: '16px', flexShrink: 0 }}>{emoji}</span>

    {/* Label */}
    <p
      style={{
        color: 'white',
        fontSize: '13px',
        fontWeight: 600,
        margin: 0,
        flex: 1,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
      }}
    >
      {label}
    </p>

    {/* Bonus */}
    <p style={{ flexShrink: 0, color: COLOURS.bad, fontWeight: 700, fontSize: '13px', margin: 0 }}>
      +{bonus} pts
    </p>
  </motion.div>
);

// ── Section heading ───────────────────────────────────────────────────────────
const SectionHeading = ({ children }) => (
  <p
    style={{
      color: COLOURS.grey,
      fontSize: '11px',
      fontWeight: 700,
      textTransform: 'uppercase',
      letterSpacing: '0.08em',
      marginBottom: '12px',
      marginTop: 0,
    }}
  >
    {children}
  </p>
);

// ── SVG dual-line chart ───────────────────────────────────────────────────────
// chartData: array of { gw, actual, parallel } (cumulative, length up to 38)
const ParallelChart = ({ chartData, svgWidth, svgHeight }) => {
  if (!chartData || chartData.length === 0) return null;

  const innerW = svgWidth  - PAD_LEFT - PAD_RIGHT;
  const innerH = svgHeight - PAD_TOP  - PAD_BOTTOM;

  const maxY   = Math.max(...chartData.map((d) => d.parallel), 1);
  const yRange = maxY;

  // Map GW and cumulative total to SVG coordinates
  const toX = (gw)  => PAD_LEFT + ((gw - 1) / 37) * innerW;
  const toY = (val) => PAD_TOP  + (1 - val / yRange) * innerH;

  const actualPts   = chartData.map((d) => `${toX(d.gw)},${toY(d.actual)}`).join(' ');
  const parallelPts = chartData.map((d) => `${toX(d.gw)},${toY(d.parallel)}`).join(' ');

  // Gap fill polygon: trace parallel forward then actual backward
  const gapPolygon = [
    ...chartData.map((d) => `${toX(d.gw)},${toY(d.parallel)}`),
    ...chartData.slice().reverse().map((d) => `${toX(d.gw)},${toY(d.actual)}`),
  ].join(' ');

  // Y-axis ticks
  const yTicks = [0, 0.25, 0.5, 0.75, 1.0].map((frac) => ({
    val: Math.round(frac * maxY),
    y:   toY(frac * maxY),
  }));

  // X-axis labels
  const xLabels = [1, 10, 20, 30, 38].map((gw) => ({ gw, x: toX(gw) }));

  const lastPoint     = chartData[chartData.length - 1];
  const endX          = toX(lastPoint.gw) + 8;
  const endYActual    = toY(lastPoint.actual);
  const endYParallel  = toY(lastPoint.parallel);

  return (
    <svg
      width={svgWidth}
      height={svgHeight}
      style={{ display: 'block', overflow: 'visible' }}
      aria-label="Parallel universe cumulative points chart showing actual vs phantom season"
    >
      {/* ── Y-axis grid lines and labels ─────────────────────────────────── */}
      {yTicks.map(({ val, y }) => (
        <g key={val}>
          <line
            x1={PAD_LEFT} y1={y} x2={svgWidth - PAD_RIGHT} y2={y}
            stroke={COLOURS.border} strokeWidth={0.5} strokeDasharray="3 4" opacity={0.5}
          />
          <text
            x={PAD_LEFT - 6} y={y + 4}
            textAnchor="end" fill={COLOURS.grey} fontSize={9} fontFamily="Roboto, sans-serif"
          >
            {val}
          </text>
        </g>
      ))}

      {/* ── X-axis labels ────────────────────────────────────────────────── */}
      {xLabels.map(({ gw, x }) => (
        <text
          key={gw}
          x={x} y={svgHeight - 4}
          textAnchor="middle" fill={COLOURS.grey} fontSize={9} fontFamily="Roboto, sans-serif"
        >
          GW{gw}
        </text>
      ))}

      {/* ── Gap fill polygon — combined missed points area ────────────────── */}
      <polygon points={gapPolygon} fill="rgba(235,87,87,0.12)" stroke="none" />

      {/* ── Actual line — solid turquoise ─────────────────────────────────── */}
      <polyline
        points={actualPts} fill="none"
        stroke={COLOURS.turquoise} strokeWidth={2}
        strokeLinecap="round" strokeLinejoin="round"
      />

      {/* ── Parallel (phantom) line — dashed white/grey ───────────────────── */}
      <polyline
        points={parallelPts} fill="none"
        stroke="rgba(255,255,255,0.55)" strokeWidth={1.5}
        strokeDasharray="5 4" strokeLinecap="round" strokeLinejoin="round"
      />

      {/* ── End point dots ───────────────────────────────────────────────── */}
      <circle cx={toX(lastPoint.gw)} cy={endYActual}   r={3} fill={COLOURS.turquoise} />
      <circle cx={toX(lastPoint.gw)} cy={endYParallel} r={3} fill="rgba(255,255,255,0.7)" />

      {/* ── End labels ───────────────────────────────────────────────────── */}
      <text
        x={endX} y={endYActual + 4}
        fill={COLOURS.turquoise} fontSize={10} fontWeight="700" fontFamily="Roboto, sans-serif"
      >
        {lastPoint.actual}
      </text>
      <text
        x={endX} y={endYParallel + 4}
        fill="rgba(255,255,255,0.65)" fontSize={10} fontWeight="600" fontFamily="Roboto, sans-serif"
      >
        Phantom
      </text>

      {/* ── Legend ───────────────────────────────────────────────────────── */}
      <g>
        <line
          x1={PAD_LEFT + 12} y1={PAD_TOP - 8} x2={PAD_LEFT + 30} y2={PAD_TOP - 8}
          stroke={COLOURS.turquoise} strokeWidth={2}
        />
        <text
          x={PAD_LEFT + 34} y={PAD_TOP - 4}
          fill={COLOURS.grey} fontSize={9} fontFamily="Roboto, sans-serif"
        >
          Actual
        </text>
        <line
          x1={PAD_LEFT + 77} y1={PAD_TOP - 8} x2={PAD_LEFT + 95} y2={PAD_TOP - 8}
          stroke="rgba(255,255,255,0.55)" strokeWidth={1.5} strokeDasharray="5 4"
        />
        <text
          x={PAD_LEFT + 99} y={PAD_TOP - 4}
          fill={COLOURS.grey} fontSize={9} fontFamily="Roboto, sans-serif"
        >
          Phantom Season
        </text>
      </g>
    </svg>
  );
};

// ── Main component ────────────────────────────────────────────────────────────
const BenchHaunting = () => {
  const { data } = useLeagueData();
  const { selectedManager } = useManagerContext();

  // ── Responsive chart width via ResizeObserver ─────────────────────────────
  const wrapperRef = useRef(null);
  const [svgWidth, setSvgWidth] = useState(640);

  const handleResize = useCallback((entries) => {
    const w = entries[0]?.contentRect?.width;
    if (w && w > 0) setSvgWidth(Math.floor(w));
  }, []);

  useEffect(() => {
    if (!wrapperRef.current) return;
    const ro = new ResizeObserver(handleResize);
    ro.observe(wrapperRef.current);
    return () => ro.disconnect();
  }, [handleResize]);

  const svgHeight = Math.round(svgWidth * 0.55);

  // ── Filter raw bench haunting data to selected manager ────────────────────
  const rawHaunting = useMemo(() => {
    if (!data?.benchHaunting || !selectedManager) return [];
    return data.benchHaunting.filter((r) => r.Manager === selectedManager);
  }, [data, selectedManager]);

  // ── Filter captain choices data to selected manager ───────────────────────
  // captainHindsight.details holds the per-GW rows from 'Detailed Captain Choices'
  // with fields: Manager, gw, actual_points, optimal_points, points_diff
  const rawCaptainChoices = useMemo(() => {
    if (!data?.captainHindsight?.details || !selectedManager) return [];
    return data.captainHindsight.details.filter((r) => r.Manager === selectedManager);
  }, [data, selectedManager]);

  // ── Compute parallel universe combining both bonus streams ────────────────
  const {
    chartData,
    benchPhantom,
    captainPhantom,
    totalPhantom,
    actualFinalTotal,
    parallelFinalTotal,
    hauntedGwCount,
    topCaptainRegrets,
    topBenchGhosts,
  } = useMemo(() => {
    const EMPTY = {
      chartData:          [],
      benchPhantom:       0,
      captainPhantom:     0,
      totalPhantom:       0,
      actualFinalTotal:   0,
      parallelFinalTotal: 0,
      worstBenchGw:       null,
      worstCaptainGw:     null,
      hauntedGwCount:     0,
      topCaptainRegrets:  [],
      topBenchGhosts:     [],
    };

    if (!selectedManager || !data?.weeklyPoints) return EMPTY;

    // 1. Locate weekly points row for this manager
    const weeklyRow = data.weeklyPoints.find((r) => r.Manager === selectedManager);
    if (!weeklyRow) return EMPTY;

    // 2. Bench bonus per GW — maximum haunting gap where Is_Haunting is true
    const benchBonusByGw = {};  // { [gw: number]: bonus }
    const benchEventByGw = {};  // { [gw: number]: best haunting row }

    rawHaunting.forEach((row) => {
      // Normalise Is_Haunting defensively (may arrive as boolean, string, or number)
      const isHaunting =
        row.Is_Haunting === true ||
        row.Is_Haunting === 'true' ||
        row.Is_Haunting === 1;
      if (!isHaunting) return;

      const gw    = Number(row.GW);
      const bonus = Number(row.Player_Points) - Number(row.Lowest_Starter_Points);
      if (!isFinite(gw) || !isFinite(bonus) || bonus <= 0) return;

      if ((benchBonusByGw[gw] ?? -Infinity) < bonus) {
        benchBonusByGw[gw] = bonus;
        benchEventByGw[gw] = row;
      }
    });

    // 3. Captain bonus per GW — points_diff = optimal - actual (positive = missed out)
    const captainBonusByGw = {};  // { [gw: number]: bonus }

    rawCaptainChoices.forEach((row) => {
      const gw    = Number(row.gw);
      const bonus = Math.max(0, Number(row.points_diff) || 0);
      if (!isFinite(gw) || bonus <= 0) return;
      captainBonusByGw[gw] = bonus;
    });

    // 4. Combined bonus per GW (both streams summed)
    const allGwKeys = new Set([
      ...Object.keys(benchBonusByGw).map(Number),
      ...Object.keys(captainBonusByGw).map(Number),
    ]);
    const totalBonusByGw = {};
    allGwKeys.forEach((gw) => {
      totalBonusByGw[gw] = (benchBonusByGw[gw] || 0) + (captainBonusByGw[gw] || 0);
    });

    // 5. Build GW-by-GW cumulative chart data for GW 1..38
    let runActual   = 0;
    let runParallel = 0;
    const gwData    = [];

    for (let gw = 1; gw <= 38; gw++) {
      // GW keys in weeklyPoints can be numeric strings or numbers — try both
      const rawPts = weeklyRow[gw] ?? weeklyRow[String(gw)] ?? 0;
      const actual = isFinite(Number(rawPts)) ? Number(rawPts) : 0;
      const bonus  = totalBonusByGw[gw] ?? 0;

      runActual   += actual;
      runParallel += actual + bonus;

      gwData.push({ gw, actual: runActual, parallel: runParallel });
    }

    // 6. Aggregate stats
    const benchEntries   = Object.entries(benchBonusByGw).map(([gw, b]) => ({ gw: Number(gw), bonus: b }));
    const captainEntries = Object.entries(captainBonusByGw).map(([gw, b]) => ({ gw: Number(gw), bonus: b }));
    const totalEntries   = Object.entries(totalBonusByGw).map(([gw, b]) => ({ gw: Number(gw), bonus: b }));

    const sumBench   = benchEntries.reduce((s, e) => s + e.bonus, 0);
    const sumCaptain = captainEntries.reduce((s, e) => s + e.bonus, 0);
    const sumTotal   = sumBench + sumCaptain;

    const worstBench   = benchEntries.sort((a, b) => b.bonus - a.bonus)[0] ?? null;
    const worstCaptain = captainEntries.sort((a, b) => b.bonus - a.bonus)[0] ?? null;

    // Count GWs where combined missed points (bench + captain) is >= 10
    const hauntCount = totalEntries.filter((e) => e.bonus >= 10).length;

    // Top 3 captain regrets (by captainBonusByGw desc)
    const topCaptain = captainEntries
      .sort((a, b) => b.bonus - a.bonus)
      .slice(0, 3);

    // Top 3 bench ghosts (by benchBonusByGw desc)
    const topBench = benchEntries
      .sort((a, b) => b.bonus - a.bonus)
      .slice(0, 3)
      .map((e) => ({ ...e, row: benchEventByGw[e.gw] }));

    const finalRow = gwData[gwData.length - 1] ?? { actual: 0, parallel: 0 };

    return {
      chartData:          gwData,
      benchPhantom:       Math.round(sumBench),
      captainPhantom:     Math.round(sumCaptain),
      totalPhantom:       Math.round(sumTotal),
      actualFinalTotal:   finalRow.actual,
      parallelFinalTotal: finalRow.parallel,
      worstBenchGw:       worstBench?.gw ?? null,
      worstCaptainGw:     worstCaptain?.gw ?? null,
      hauntedGwCount:     hauntCount,
      topCaptainRegrets:  topCaptain,
      topBenchGhosts:     topBench,
    };
  }, [data, selectedManager, rawHaunting, rawCaptainChoices]);

  // ── Guards ────────────────────────────────────────────────────────────────
  if (!selectedManager || !data) return null;

  const hasData = chartData.length > 0 && (rawHaunting.length > 0 || rawCaptainChoices.length > 0);

  if (!hasData) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        style={{
          backgroundColor: COLOURS.surface1,
          border: `1px solid ${COLOURS.border}`,
          borderRadius: '14px',
          padding: '32px 24px',
          textAlign: 'center',
          maxWidth: '720px',
          margin: '0 auto',
        }}
      >
        <p style={{ fontSize: '28px', marginBottom: '12px' }}>👻</p>
        <h3 style={{ color: 'white', fontWeight: 700, fontSize: '16px', marginBottom: '8px' }}>
          No haunting data available
        </h3>
        <p style={{ color: COLOURS.grey, fontSize: '14px' }}>
          Run the latest scraper to unlock The Haunting view.
        </p>
      </motion.div>
    );
  }

  return (
    <div style={{ padding: '24px 32px', maxWidth: '720px', margin: '0 auto' }}>

      {/* ── Hero section ──────────────────────────────────────────────────────── */}
      <motion.div
        initial={{ opacity: 0, y: -12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.45 }}
      >
        {/* Label */}
        <p
          style={{
            color: COLOURS.grey,
            fontSize: '11px',
            fontWeight: 700,
            textTransform: 'uppercase',
            letterSpacing: '0.1em',
            margin: '0 0 6px',
          }}
        >
          Your Phantom Season Total
        </p>

        {/* Big number */}
        <div style={{ lineHeight: 1 }}>
          <AnimatedCounter target={parallelFinalTotal} />
        </div>

        {/* Subtitle */}
        <p style={{ color: COLOURS.grey, fontSize: '14px', margin: '8px 0 0', lineHeight: 1.6 }}>
          <span style={{ color: 'white', fontWeight: 600 }}>{fmtPts(actualFinalTotal)}</span>{' '}
          actual +{' '}
          <span style={{ color: COLOURS.bad, fontWeight: 600 }}>{fmtPts(totalPhantom)} left on the table</span>
        </p>

        {/* Descriptor */}
        <p style={{ color: COLOURS.grey, fontSize: '12px', margin: '10px 0 0', lineHeight: 1.55 }}>
          The <span style={{ color: 'white' }}>haunting</span> is the gap between your season and the parallel one where you always picked the right captain and your best bench player came on when needed. It doesn't penalise your transfers — only the weekly decisions that were in your hands each gameweek.
        </p>

        {/* Breakdown chips — bench ghost and captain regret contributions */}
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginTop: '12px' }}>
          <span
            style={{
              padding: '3px 10px',
              borderRadius: '20px',
              backgroundColor: 'rgba(138,138,138,0.12)',
              border: `1px solid ${COLOURS.border}`,
              fontSize: '12px',
              fontWeight: 600,
              color: COLOURS.grey,
            }}
          >
            👻 {fmtPts(benchPhantom)} bench
          </span>
          <span
            style={{
              padding: '3px 10px',
              borderRadius: '20px',
              backgroundColor: 'rgba(138,138,138,0.12)',
              border: `1px solid ${COLOURS.border}`,
              fontSize: '12px',
              fontWeight: 600,
              color: COLOURS.grey,
            }}
          >
            ⚡ {fmtPts(captainPhantom)} captain
          </span>
        </div>

        {/* Manager name */}
        <p style={{ color: COLOURS.blue, fontSize: '13px', fontWeight: 700, margin: '10px 0 0' }}>
          {selectedManager}
        </p>
      </motion.div>

      {/* ── Chart ─────────────────────────────────────────────────────────────── */}
      <motion.div
        ref={wrapperRef}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.5, delay: 0.1 }}
        style={{
          marginTop: '16px',
          backgroundColor: COLOURS.surface1,
          border: `1px solid ${COLOURS.border}`,
          borderRadius: '14px',
          padding: '16px',
        }}
      >
        <ParallelChart
          chartData={chartData}
          svgWidth={svgWidth - 32}  /* subtract card padding (16px each side) */
          svgHeight={svgHeight}
        />
      </motion.div>

      {/* ── Square stat cards ─────────────────────────────────────────────────── */}
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, delay: 0.2 }}
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: '10px',
          justifyContent: 'center',
          marginTop: '28px',
        }}
      >
        {/* 1. Total phantom points */}
        <SquareStatCard
          value={`+${fmtPts(totalPhantom)}`}
          valueColour={COLOURS.bad}
          label="missed this season"
        />
        {/* 2. Captain regrets */}
        <SquareStatCard
          value={`+${fmtPts(captainPhantom)}`}
          valueColour={COLOURS.bad}
          label="wrong armband"
        />
        {/* 3. Bench ghosts */}
        <SquareStatCard
          value={`+${fmtPts(benchPhantom)}`}
          valueColour={COLOURS.bad}
          label="left on bench"
        />
        {/* 4. GWs with 10+ combined points missed */}
        <SquareStatCard
          value={hauntedGwCount}
          valueColour={COLOURS.grey}
          label="GWs with 10+ pts missed"
        />
      </motion.div>

      {/* ── Worst hauntings — two compact sections ────────────────────────────── */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.35, delay: 0.3 }}
        style={{ marginTop: '28px', maxWidth: '480px', margin: '28px auto 0' }}
      >

        {/* Captain Regrets section */}
        {topCaptainRegrets.length > 0 && (
          <div style={{ marginBottom: '28px' }}>
            <SectionHeading>Captain Regrets</SectionHeading>
            <motion.div
              variants={{
                hidden:  {},
                visible: { transition: { staggerChildren: 0.07 } },
              }}
              initial="hidden"
              animate="visible"
              style={{ display: 'flex', flexDirection: 'column', gap: '7px' }}
            >
              {topCaptainRegrets.map(({ gw, bonus }) => (
                <EventRow
                  key={`captain-${gw}`}
                  emoji="⚡"
                  label={`GW${gw}`}
                  bonus={bonus}
                />
              ))}
            </motion.div>
          </div>
        )}

        {/* Bench Ghosts section */}
        {topBenchGhosts.length > 0 && (
          <div>
            <SectionHeading>Bench Ghosts</SectionHeading>
            <motion.div
              variants={{
                hidden:  {},
                visible: { transition: { staggerChildren: 0.07 } },
              }}
              initial="hidden"
              animate="visible"
              style={{ display: 'flex', flexDirection: 'column', gap: '7px' }}
            >
              {topBenchGhosts.map(({ gw, bonus, row }) => (
                <EventRow
                  key={`bench-${gw}`}
                  emoji="👻"
                  label={row?.Player_Name ? `${row.Player_Name} (GW${gw})` : `GW${gw}`}
                  bonus={bonus}
                />
              ))}
            </motion.div>
          </div>
        )}

      </motion.div>

    </div>
  );
};

export default BenchHaunting;
